
import { useState, useRef, useCallback } from 'react';
import { supabase } from '@/integrations/supabase/client';

export const useTextToSpeech = () => {
  const [isSpeaking, setIsSpeaking] = useState(false);
  const [isMuted, setIsMuted] = useState(false);
  const audioRef = useRef<HTMLAudioElement | null>(null);
  const timeoutRef = useRef<NodeJS.Timeout | null>(null);

  const stopCurrentSpeech = useCallback(() => {
    console.log('Stopping current speech');
    if (audioRef.current) {
      audioRef.current.pause();
      audioRef.current.currentTime = 0;
      audioRef.current = null;
    }
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
      timeoutRef.current = null;
    }
    setIsSpeaking(false);
  }, []);

  const convertTextToSpeech = useCallback(async (text: string) => {
    if (!text.trim() || isMuted) return null;

    try {
      console.log('Converting text to speech:', text.substring(0, 50) + '...');
      
      const { data, error } = await supabase.functions.invoke('text-to-speech', {
        body: { 
          text: text.trim(),
          voice: 'nova'
        }
      });

      if (error) {
        console.error('Error generating speech:', error);
        return null;
      }

      if (data?.audioContent) {
        console.log('Successfully received audio content');
        return data.audioContent;
      }

      console.warn('No audio content received');
      return null;
    } catch (error) {
      console.error('Error in convertTextToSpeech:', error);
      return null;
    }
  }, [isMuted]);

  const playAudio = useCallback(async (audioContent: string) => {
    if (isMuted) {
      console.log('Audio is muted, skipping playback');
      return;
    }

    try {
      // Stop any current speech first
      stopCurrentSpeech();

      console.log('Creating audio blob from base64 content');
      
      // Create audio blob from base64
      const audioBlob = new Blob(
        [Uint8Array.from(atob(audioContent), c => c.charCodeAt(0))],
        { type: 'audio/mpeg' }
      );
      
      const audioUrl = URL.createObjectURL(audioBlob);
      const audio = new Audio(audioUrl);
      audioRef.current = audio;

      console.log('Audio created, starting playback immediately');
      setIsSpeaking(true);

      // Set up cleanup function
      const cleanup = () => {
        URL.revokeObjectURL(audioUrl);
        audioRef.current = null;
        setIsSpeaking(false);
      };

      // Set up event listeners
      audio.onended = () => {
        console.log('Audio playback finished');
        cleanup();
      };

      audio.onerror = (error) => {
        console.error('Audio playback error:', error);
        cleanup();
      };

      // Start playback immediately
      await audio.play();
      console.log('Audio playback started successfully');

    } catch (error) {
      console.error('Error playing audio:', error);
      setIsSpeaking(false);
      throw error;
    }
  }, [isMuted, stopCurrentSpeech]);

  const speakText = useCallback(async (text: string, delayMs: number = 500) => {
    if (!text.trim() || isMuted) {
      console.log('Skipping speech - text empty or muted');
      return;
    }

    // Clear any existing timeout
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
      timeoutRef.current = null;
    }

    console.log('Scheduling speech with delay:', delayMs, 'for text:', text.substring(0, 30) + '...');

    // Wait for the specified delay before speaking
    timeoutRef.current = setTimeout(async () => {
      if (!isMuted) {
        try {
          console.log('Starting text-to-speech conversion for:', text.substring(0, 30) + '...');
          const audioContent = await convertTextToSpeech(text);
          if (audioContent && !isMuted) {
            console.log('Audio content received, starting playback');
            await playAudio(audioContent);
          } else {
            console.log('No audio content or muted, skipping playback');
          }
        } catch (error) {
          console.error('Error in speakText:', error);
          setIsSpeaking(false);
        }
      }
      timeoutRef.current = null;
    }, delayMs);
  }, [convertTextToSpeech, playAudio, isMuted]);

  const toggleMute = useCallback(() => {
    const newMutedState = !isMuted;
    setIsMuted(newMutedState);
    
    if (newMutedState && isSpeaking) {
      stopCurrentSpeech();
    }
    
    console.log('Text-to-speech muted:', newMutedState);
  }, [isMuted, isSpeaking, stopCurrentSpeech]);

  // Cleanup function
  const cleanup = useCallback(() => {
    stopCurrentSpeech();
  }, [stopCurrentSpeech]);

  return {
    isSpeaking,
    isMuted,
    speakText,
    toggleMute,
    stopCurrentSpeech,
    cleanup
  };
};
